/**
 * FullviewConstellationVisualizer
 *
 * Sprint 49: Flat constellation using Cytoscape.js with concentric layout
 * seeded by entity hierarchy (divisions centred) + optional force-directed
 * refinement. No compound nodes — all entities as uniform circles in a
 * single plane.
 *
 * Sprint 58: Ownership-chain layout (Division → Department → Business Service
 * → App → Tech Service → Infra → Data). Governance overlay toggled separately.
 *
 * Features: drill-down (2-hop neighbourhood), legend, search, PNG export,
 * edge hover labels, Refine button, visual hierarchy, governance overlay.
 *
 * Follows OOA/OOD: encapsulates all rendering, interaction, and UI overlay logic.
 */

import cytoscape, { type Core, type NodeSingular } from 'cytoscape';
import coseBilkent from 'cytoscape-cose-bilkent';
import type {
    ConstellationData,
} from './constellation-data-builder';
import type { ValidationResult } from './fullview-constellation-data-builder';
import { classifyEntityType } from './entity-type-groups';

// Register layout extension once
cytoscape.use(coseBilkent);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BACKGROUND_COLOR = '#0f172a';

const ENTITY_COLORS: Record<string, string> = {
    'division': '#facc15',
    'department': '#60a5fa',
    'application': '#f59e0b',
    'infrastructure': '#10b981',
    'data': '#ef4444',
    'technical-service': '#8b5cf6',
    'hr-role': '#ec4899',
    'business-service': '#14b8a6',
    'risk': '#f97316',
    'control': '#22c55e',
    'capability': '#06b6d4',
    'process': '#a855f7',
    'process_connector': '#e11d48',
    'compliance': '#84cc16',
    'integration': '#d946ef',
    'vendor': '#0ea5e9',
    'organization': '#3b82f6',
    'technical_service': '#8b5cf6',
    'business_service': '#14b8a6',
    'hr_role': '#ec4899',
    'data_object': '#ef4444',
};

/** Sprint 58: Ownership chain — centre to edge */
const OWNERSHIP_CHAIN: string[] = [
    'division',           // Centre: owners
    'department',         // Org structure
    'business-service',   // What we deliver
    'application',        // What supports it
    'technical-service',  // Technical plumbing
    'infrastructure',     // Hardware/hosting
    'data',               // Data layer
];
const CHAIN_BASE_SCORE = 100;
const CHAIN_STEP = 12;

const OVERFLOW_PALETTE: string[] = [
    '#06b6d4', '#a855f7', '#e11d48', '#84cc16',
    '#d946ef', '#0ea5e9', '#f43f5e', '#34d399',
    '#fbbf24', '#818cf8', '#fb923c', '#2dd4bf',
];

const dynamicColorMap = new Map<string, string>();
let overflowIdx = 0;

function getEntityColor(entityType: string): string {
    const normalized = entityType.toLowerCase().replace(/\s+/g, '-');
    if (ENTITY_COLORS[normalized]) return ENTITY_COLORS[normalized];
    if (dynamicColorMap.has(normalized)) return dynamicColorMap.get(normalized)!;
    const color = OVERFLOW_PALETTE[overflowIdx % OVERFLOW_PALETTE.length];
    overflowIdx++;
    dynamicColorMap.set(normalized, color);
    return color;
}

function darken(hex: string, amount = 0.3): string {
    const num = parseInt(hex.slice(1), 16);
    const r = Math.max(0, ((num >> 16) & 0xff) - Math.round(255 * amount));
    const g = Math.max(0, ((num >> 8) & 0xff) - Math.round(255 * amount));
    const b = Math.max(0, (num & 0xff) - Math.round(255 * amount));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

// ---------------------------------------------------------------------------
// FullviewConstellationVisualizer
// ---------------------------------------------------------------------------

export class FullviewConstellationVisualizer {
    private containerId: string;
    private container: HTMLElement | null = null;
    private cy: Core | null = null;
    private hiddenTypes = new Set<string>();
    private isDrilledDown = false;
    private drillStack: { nodeId: string; label: string }[] = [];

    // Sprint 58: Governance overlay state
    private governanceVisible = false;

    constructor(containerId: string) {
        this.containerId = containerId;
    }

    // -----------------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------------

    async render(data: ConstellationData): Promise<void> {
        this.container = document.getElementById(this.containerId);
        if (!this.container) throw new Error(`Container #${this.containerId} not found`);

        // Reset dynamic colors
        dynamicColorMap.clear();
        overflowIdx = 0;

        this.setupContainer();
        this.showLoading(data);
        this.createCytoscape(data);
        this.runConcentricLayout();
        // Sprint 58: hide governance nodes on initial render
        this.applyGovernanceVisibility();
        this.hideLoading();
        this.buildOverlays(data);
    }

    renderError(validation: ValidationResult): void {
        this.container = document.getElementById(this.containerId);
        if (!this.container) return;

        this.container.style.backgroundColor = BACKGROUND_COLOR;
        this.container.style.position = 'relative';
        this.container.style.overflow = 'auto';

        const panel = document.createElement('div');
        panel.style.cssText =
            'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);' +
            'background:#1e293b;border:1px solid #334155;border-radius:12px;padding:32px;' +
            'max-width:520px;width:90%;color:#e2e8f0;font-family:system-ui,sans-serif;';

        panel.innerHTML = `
            <h2 style="margin:0 0 16px;font-size:18px;color:#f8fafc;">
                Constellation Cannot Be Rendered
            </h2>
            ${validation.errors.map((e) => `
                <div style="margin:8px 0;padding:10px 12px;background:#450a0a;border-radius:8px;border-left:3px solid #ef4444;">
                    <span style="margin-right:6px;">X</span>${this.escapeHtml(e)}
                </div>
            `).join('')}
            ${validation.warnings.map((w) => `
                <div style="margin:8px 0;padding:10px 12px;background:#422006;border-radius:8px;border-left:3px solid #f59e0b;">
                    <span style="margin-right:6px;">!</span>${this.escapeHtml(w)}
                </div>
            `).join('')}
            <p style="margin:16px 0 0;font-size:13px;color:#94a3b8;">
                Ensure your import session includes at least 2 entities. Add relationship
                data via an Integrations sheet for a connected map.
            </p>
        `;
        this.container.innerHTML = '';
        this.container.appendChild(panel);
    }

    destroy(): void {
        this.hideDetail();
        if (this.cy) {
            this.cy.destroy();
            this.cy = null;
        }
        if (this.container) {
            this.container.innerHTML = '';
        }
        this.hiddenTypes.clear();
        this.isDrilledDown = false;
        this.drillStack = [];
        this.governanceVisible = false;
    }

    exportPNG(): void {
        if (!this.cy) return;
        const png = this.cy.png({ scale: 2, bg: BACKGROUND_COLOR, full: true });
        const link = document.createElement('a');
        link.href = png;
        link.download = 'constellation-fullview.png';
        link.click();
    }

    /**
     * Sprint 57: Highlight nodes whose entityType is in the given set;
     * dim everything else. Edges are highlighted only when both endpoints
     * are in the highlighted set.
     */
    applyGroupHighlight(highlightedTypes: Set<string>): void {
        if (!this.cy) return;

        this.cy.nodes().forEach((n) => {
            const et = (n.data('entityType') as string || '').toLowerCase().replace(/\s+/g, '-');
            if (highlightedTypes.has(et)) {
                n.removeClass('group-dimmed').addClass('group-highlighted');
            } else {
                n.removeClass('group-highlighted').addClass('group-dimmed');
            }
        });

        this.cy.edges().forEach((e) => {
            const srcType = (e.source().data('entityType') as string || '').toLowerCase().replace(/\s+/g, '-');
            const tgtType = (e.target().data('entityType') as string || '').toLowerCase().replace(/\s+/g, '-');
            if (highlightedTypes.has(srcType) && highlightedTypes.has(tgtType)) {
                e.removeClass('group-dimmed').addClass('group-highlighted');
            } else {
                e.removeClass('group-highlighted').addClass('group-dimmed');
            }
        });
    }

    /** Sprint 57: Remove all group highlight/dim classes */
    clearGroupHighlight(): void {
        this.cy?.elements().removeClass('group-dimmed group-highlighted');
    }

    /**
     * Sprint 57: Toggle a single entity type's visibility via the
     * existing hiddenTypes mechanism.
     */
    toggleSingleEntityType(type: string): void {
        if (this.hiddenTypes.has(type)) {
            this.hiddenTypes.delete(type);
        } else {
            this.hiddenTypes.add(type);
        }
        this.applyHiddenTypes();
    }

    /** Sprint 57: Check if an entity type is currently hidden */
    isEntityTypeHidden(type: string): boolean {
        return this.hiddenTypes.has(type);
    }

    // -----------------------------------------------------------------------
    // Sprint 58: Governance overlay
    // -----------------------------------------------------------------------

    /** Toggle governance overlay visibility */
    toggleGovernanceOverlay(visible: boolean): void {
        this.governanceVisible = visible;
        this.applyGovernanceVisibility();
    }

    /** Check if governance overlay is currently visible */
    isGovernanceOverlayVisible(): boolean {
        return this.governanceVisible;
    }

    private applyGovernanceVisibility(): void {
        if (!this.cy) return;

        const govNodes = this.cy.nodes().filter(n => n.data('isGovernance') === true);
        if (govNodes.length === 0) return;

        if (!this.governanceVisible) {
            // Hide governance nodes — edges implicitly hidden by Cytoscape
            govNodes.style('display', 'none');
            // Also hide edges touching governance nodes
            govNodes.connectedEdges().style('display', 'none');
        } else {
            // Show governance nodes
            govNodes.style('display', 'element');
            govNodes.connectedEdges().style('display', 'element');

            // Reposition each governance node near the average position of its
            // connected non-governance nodes
            govNodes.forEach(govNode => {
                const connectedNonGov = govNode.connectedEdges()
                    .connectedNodes()
                    .filter(n => n.data('isGovernance') !== true && n.id() !== govNode.id());

                if (connectedNonGov.length > 0) {
                    let avgX = 0, avgY = 0;
                    connectedNonGov.forEach(n => {
                        const pos = n.position();
                        avgX += pos.x;
                        avgY += pos.y;
                    });
                    avgX /= connectedNonGov.length;
                    avgY /= connectedNonGov.length;

                    // Offset 40-70px in a random direction
                    const offset = 40 + Math.random() * 30;
                    const angle = Math.random() * 2 * Math.PI;
                    govNode.position({
                        x: avgX + offset * Math.cos(angle),
                        y: avgY + offset * Math.sin(angle),
                    });
                }

                // Mark edges touching governance nodes with governance-edge class
                govNode.connectedEdges().addClass('governance-edge');
            });
        }
    }

    // -----------------------------------------------------------------------
    // Loading overlay
    // -----------------------------------------------------------------------

    private showLoading(data: ConstellationData): void {
        const overlay = document.createElement('div');
        overlay.id = 'fv-loading';
        overlay.style.cssText =
            'position:absolute;top:0;left:0;right:0;bottom:0;z-index:100;' +
            `background:${BACKGROUND_COLOR};display:flex;flex-direction:column;` +
            'align-items:center;justify-content:center;gap:16px;';
        overlay.innerHTML = `
            <div style="width:48px;height:48px;border:3px solid #334155;border-top-color:#3b82f6;border-radius:50%;animation:fv-spin 0.8s linear infinite;"></div>
            <div style="font-size:24px;font-weight:700;color:#f1f5f9;">Loading data...</div>
            <div style="font-size:14px;color:#94a3b8;">Laying out ${data.nodes.length} nodes and ${data.edges.length} edges...</div>
        `;
        const style = document.createElement('style');
        style.textContent = '@keyframes fv-spin { to { transform: rotate(360deg); } }';
        overlay.appendChild(style);
        this.container!.appendChild(overlay);
    }

    private hideLoading(): void {
        document.getElementById('fv-loading')?.remove();
    }

    // -----------------------------------------------------------------------
    // Setup
    // -----------------------------------------------------------------------

    private setupContainer(): void {
        const el = this.container!;
        el.style.backgroundColor = BACKGROUND_COLOR;
        el.style.position = 'relative';
        el.style.overflow = 'hidden';
        el.innerHTML = '';
    }

    // -----------------------------------------------------------------------
    // Cytoscape init — preset layout (positions set in runConcentricLayout)
    // -----------------------------------------------------------------------

    private createCytoscape(data: ConstellationData): void {
        const elements = this.buildElements(data);

        this.cy = cytoscape({
            container: this.container,
            elements,
            style: this.buildStylesheet(),
            layout: { name: 'preset' },
            minZoom: 0.05,
            maxZoom: 8,
            wheelSensitivity: 0.25,
        });

        this.bindInteractions();
    }

    private buildElements(data: ConstellationData): cytoscape.ElementDefinition[] {
        const nodes: cytoscape.ElementDefinition[] = data.nodes.map((n) => {
            const isGovernance = classifyEntityType(n.entityType) === 'governance';
            return {
                group: 'nodes' as const,
                data: {
                    id: n.id,
                    label: n.label,
                    entityType: n.entityType,
                    color: getEntityColor(n.entityType),
                    isGovernance,
                    ...n.metadata,
                },
                classes: isGovernance ? 'governance-node' : undefined,
            };
        });

        const edges: cytoscape.ElementDefinition[] = data.edges.map((e) => ({
            group: 'edges' as const,
            data: {
                id: e.id,
                source: e.source,
                target: e.target,
                weight: e.weight || 1,
                relationshipType: e.relationshipType,
            },
        }));

        return [...nodes, ...edges];
    }

    // -----------------------------------------------------------------------
    // Concentric layout with ownership chain scoring
    // -----------------------------------------------------------------------

    private runConcentricLayout(): void {
        if (!this.cy) return;

        // Build chain rank lookup (including underscore variants)
        const chainRank: Record<string, number> = {};
        OWNERSHIP_CHAIN.forEach((t, i) => {
            const score = CHAIN_BASE_SCORE - i * CHAIN_STEP;
            chainRank[t] = score;
            // Add alternate form
            if (t.includes('-')) chainRank[t.replace(/-/g, '_')] = score;
        });

        // Exclude governance nodes from layout when overlay is hidden
        const isExcluded = (n: NodeSingular): boolean => {
            return !this.governanceVisible && n.data('isGovernance') === true;
        };

        const connected = this.cy.nodes().filter(n =>
            n.connectedEdges().length > 0 && !isExcluded(n),
        );
        const disconnected = this.cy.nodes().filter(n =>
            n.connectedEdges().length === 0 && !isExcluded(n),
        );

        // Phase 1: concentric layout on connected nodes
        if (connected.length > 0) {
            connected.layout({
                name: 'concentric',
                concentric: (node: any) => {
                    const et = (node.data('entityType') as string || '').toLowerCase().replace(/\s+/g, '-');
                    const degreeBonus = node.connectedEdges().length;

                    // Types in ownership chain get their chain rank
                    if (chainRank[et] !== undefined) {
                        return chainRank[et] + degreeBonus;
                    }

                    // Non-chain types: score by best-connected chain neighbour
                    let bestNeighbourScore = 0;
                    node.connectedEdges().forEach((edge: any) => {
                        const other = edge.source().id() === node.id() ? edge.target() : edge.source();
                        const otherType = (other.data('entityType') as string || '').toLowerCase().replace(/\s+/g, '-');
                        if (chainRank[otherType] !== undefined && chainRank[otherType] > bestNeighbourScore) {
                            bestNeighbourScore = chainRank[otherType];
                        }
                    });

                    // Place near neighbour but half-step lower
                    return bestNeighbourScore > 0
                        ? bestNeighbourScore - CHAIN_STEP / 2
                        : degreeBonus;
                },
                levelWidth: () => 3,
                minNodeSpacing: 50,
                animate: false,
                fit: false,
                padding: 60,
            } as any).run();

            // Add jitter — tighter for inner chain positions, looser outward
            connected.forEach((n) => {
                const pos = n.position();
                const et = (n.data('entityType') as string || '').toLowerCase().replace(/\s+/g, '-');
                const chainIdx = OWNERSHIP_CHAIN.indexOf(et);
                let jitter: number;
                if (chainIdx === 0) {
                    jitter = 10 + Math.random() * 20; // division: tight
                } else if (chainIdx === 1) {
                    jitter = 30 + Math.random() * 50; // department
                } else if (chainIdx > 1) {
                    jitter = 40 + Math.random() * 60; // rest of chain
                } else {
                    jitter = 60 + Math.random() * 80; // non-chain
                }
                const angle = Math.random() * 2 * Math.PI;
                n.position({
                    x: pos.x + jitter * Math.cos(angle),
                    y: pos.y + jitter * Math.sin(angle),
                });
            });
        }

        // Phase 2: scatter disconnected nodes in a ring outside
        if (disconnected.length > 0 && connected.length > 0) {
            const bb = connected.boundingBox();
            const cx = (bb.x1 + bb.x2) / 2;
            const centY = (bb.y1 + bb.y2) / 2;
            const radius = Math.max(bb.w, bb.h) / 2 + 250;

            disconnected.forEach((node, i) => {
                const angle = (2 * Math.PI * i) / disconnected.length;
                const r = radius + Math.random() * 100;
                node.position({
                    x: cx + r * Math.cos(angle),
                    y: centY + r * Math.sin(angle),
                });
            });
        }

        this.cy.fit(this.cy.elements(), 60);
    }

    // -----------------------------------------------------------------------
    // Force-directed refinement (opt-in via Refine button)
    // -----------------------------------------------------------------------

    private runForceRefinement(btn: HTMLButtonElement): void {
        if (!this.cy) return;
        btn.textContent = 'Refining...';
        btn.disabled = true;

        const layout = this.cy.layout({
            name: 'cose',
            idealEdgeLength: () => 100,
            nodeRepulsion: () => 20000,
            gravity: 0.08,
            numIter: 100,
            animate: true,
            animationDuration: 1500,
            fit: true,
            padding: 60,
            nodeOverlap: 20,
            randomize: false,
        } as any);

        const done = () => {
            btn.textContent = 'Refine';
            btn.disabled = false;
        };

        layout.on('layoutstop', done);
        setTimeout(done, 30000); // fallback
        layout.run();
    }

    // -----------------------------------------------------------------------
    // Stylesheet
    // -----------------------------------------------------------------------

    private buildStylesheet(): any[] {
        return [
            // Division nodes — large, gold, prominent
            {
                selector: 'node[entityType = "division"]',
                style: {
                    'width': 50,
                    'height': 50,
                    'background-color': 'data(color)',
                    'border-width': 3,
                    'border-color': '#fef08a',
                    'label': 'data(label)',
                    'color': '#fef9c3',
                    'font-size': 14,
                    'font-weight': 'bold',
                    'text-valign': 'bottom',
                    'text-halign': 'center',
                    'text-margin-y': 8,
                    'text-max-width': '120px',
                    'text-wrap': 'ellipsis',
                    'min-zoomed-font-size': 2,
                    'text-background-color': BACKGROUND_COLOR,
                    'text-background-opacity': 0.8,
                    'text-background-padding': '3px',
                    'text-background-shape': 'roundrectangle',
                    'z-index': 100,
                    'transition-property': 'opacity, border-width',
                    'transition-duration': 200,
                } as any,
            },
            // Department nodes — medium, blue
            {
                selector: 'node[entityType = "department"]',
                style: {
                    'width': 38,
                    'height': 38,
                    'background-color': 'data(color)',
                    'border-width': 2,
                    'border-color': (ele: any) => darken(ele.data('color'), 0.15),
                    'label': 'data(label)',
                    'color': '#e2e8f0',
                    'font-size': 12,
                    'font-weight': 'bold',
                    'text-valign': 'bottom',
                    'text-halign': 'center',
                    'text-margin-y': 7,
                    'text-max-width': '100px',
                    'text-wrap': 'ellipsis',
                    'min-zoomed-font-size': 3,
                    'text-background-color': BACKGROUND_COLOR,
                    'text-background-opacity': 0.7,
                    'text-background-padding': '2px',
                    'text-background-shape': 'roundrectangle',
                    'z-index': 50,
                    'transition-property': 'opacity, border-width',
                    'transition-duration': 200,
                } as any,
            },
            // Sprint 58: Governance nodes — small diamond, dashed border
            {
                selector: 'node.governance-node',
                style: {
                    'width': 22,
                    'height': 22,
                    'shape': 'diamond',
                    'background-color': 'data(color)',
                    'border-style': 'dashed',
                    'border-width': 2,
                    'border-color': (ele: any) => darken(ele.data('color'), 0.15),
                    'label': 'data(label)',
                    'color': '#e2e8f0',
                    'font-size': 10,
                    'text-valign': 'bottom',
                    'text-halign': 'center',
                    'text-margin-y': 6,
                    'text-max-width': '100px',
                    'text-wrap': 'ellipsis',
                    'min-zoomed-font-size': 3,
                    'text-background-color': BACKGROUND_COLOR,
                    'text-background-opacity': 0.7,
                    'text-background-padding': '2px',
                    'text-background-shape': 'roundrectangle',
                    'opacity': 0.85,
                    'z-index': 30,
                    'transition-property': 'opacity, border-width',
                    'transition-duration': 200,
                } as any,
            },
            // All other nodes
            {
                selector: 'node',
                style: {
                    'width': 28,
                    'height': 28,
                    'background-color': 'data(color)',
                    'border-width': 2,
                    'border-color': (ele: any) => darken(ele.data('color'), 0.15),
                    'label': 'data(label)',
                    'color': '#e2e8f0',
                    'font-size': 11,
                    'text-valign': 'bottom',
                    'text-halign': 'center',
                    'text-margin-y': 6,
                    'text-max-width': '100px',
                    'text-wrap': 'ellipsis',
                    'min-zoomed-font-size': 3,
                    'text-background-color': BACKGROUND_COLOR,
                    'text-background-opacity': 0.7,
                    'text-background-padding': '2px',
                    'text-background-shape': 'roundrectangle',
                    'transition-property': 'opacity, border-width',
                    'transition-duration': 200,
                } as any,
            },
            // Edges
            {
                selector: 'edge',
                style: {
                    'width': (ele: any) => Math.max(1, Math.min(3, ele.data('weight') * 0.4)),
                    'line-color': '#475569',
                    'target-arrow-color': '#475569',
                    'target-arrow-shape': 'triangle',
                    'arrow-scale': 0.7,
                    'curve-style': 'bezier',
                    'opacity': 0.3,
                    'transition-property': 'opacity',
                    'transition-duration': 200,
                } as any,
            },
            // Sprint 58: Governance edges — dashed, low opacity
            {
                selector: 'edge.governance-edge',
                style: {
                    'line-style': 'dashed',
                    'opacity': 0.25,
                } as any,
            },
            // Group filter classes (Sprint 57) — before hover/search so those take precedence
            { selector: 'node.group-dimmed', style: { 'opacity': 0.15 } as any },
            { selector: 'edge.group-dimmed', style: { 'opacity': 0.06 } as any },
            { selector: 'node.group-highlighted', style: { 'opacity': 1 } as any },
            { selector: 'edge.group-highlighted', style: { 'opacity': 0.7 } as any },
            // State classes (hover/search — take precedence over group filter)
            { selector: 'node.dimmed', style: { 'opacity': 0.08 } as any },
            { selector: 'edge.dimmed', style: { 'opacity': 0.04 } as any },
            { selector: 'node.highlighted', style: { 'opacity': 1, 'border-width': 4 } as any },
            {
                selector: 'edge.highlighted',
                style: { 'opacity': 0.85, 'line-color': '#94a3b8', 'target-arrow-color': '#94a3b8' } as any,
            },
            {
                selector: 'node.search-match',
                style: {
                    'border-color': '#facc15',
                    'border-width': 5,
                    'opacity': 1,
                    'z-index': 999,
                } as any,
            },
            // Edge hover label
            {
                selector: 'edge.hover-label',
                style: {
                    'label': 'data(relationshipType)',
                    'font-size': 10,
                    'color': '#cbd5e1',
                    'text-rotation': 'autorotate',
                    'text-background-color': '#1e293b',
                    'text-background-opacity': 0.9,
                    'text-background-padding': '3px',
                    'text-background-shape': 'roundrectangle',
                    'opacity': 0.9,
                    'line-color': '#60a5fa',
                    'target-arrow-color': '#60a5fa',
                    'width': 2,
                    'z-index': 999,
                } as any,
            },
        ];
    }

    // -----------------------------------------------------------------------
    // Interactions
    // -----------------------------------------------------------------------

    private bindInteractions(): void {
        if (!this.cy) return;

        // Edge hover: show relationship label
        this.cy.on('mouseover', 'edge', (evt) => {
            evt.target.addClass('hover-label');
        });
        this.cy.on('mouseout', 'edge', (evt) => {
            evt.target.removeClass('hover-label');
        });

        // Node hover: highlight neighbourhood
        this.cy.on('mouseover', 'node', (evt) => {
            if (this.isDrilledDown) return;
            const node = evt.target as NodeSingular;
            const neighbourhood = node.closedNeighborhood();
            this.cy!.elements().addClass('dimmed');
            neighbourhood.removeClass('dimmed').addClass('highlighted');
        });

        this.cy.on('mouseout', 'node', () => {
            if (this.isDrilledDown) return;
            this.cy!.elements().removeClass('dimmed highlighted');
        });

        // Tap node: show detail panel
        this.cy.on('tap', 'node', (evt) => {
            const node = evt.target as NodeSingular;
            this.showDetail(node);
        });

        // Tap background: hide detail panel
        this.cy.on('tap', (evt) => {
            if (evt.target === this.cy) {
                this.hideDetail();
            }
        });
    }

    // -----------------------------------------------------------------------
    // Drill-down
    // -----------------------------------------------------------------------

    private drillDown(node: NodeSingular): void {
        console.log('[Constellation] drillDown called', node?.id(), node?.data('label'));
        if (!this.cy) { console.warn('[Constellation] drillDown: no cy'); return; }

        // Push to drill stack
        this.drillStack.push({ nodeId: node.id(), label: node.data('label') });

        this.applyDrillView(node);
        this.isDrilledDown = true;
        this.renderBreadcrumb();
    }

    /** Apply the 2-hop neighbourhood view centred on a node */
    private applyDrillView(node: NodeSingular): void {
        if (!this.cy) return;

        const hop1 = node.closedNeighborhood();
        const hop2 = hop1.closedNeighborhood();
        console.log('[Constellation] applyDrillView hop1:', hop1.length, 'hop2:', hop2.length);

        this.cy.elements().removeClass('dimmed highlighted');
        // Show only the 2-hop neighbourhood
        this.cy.elements().style('display', 'none');
        hop2.style('display', 'element');

        // Use concentric layout centred on the tapped node (fast, no hanging)
        hop2.layout({
            name: 'concentric',
            concentric: (n: any) => {
                if (n.id() === node.id()) return 100;
                if (hop1.contains(n)) return 50;
                return 10;
            },
            levelWidth: () => 1,
            minNodeSpacing: 60,
            animate: true,
            animationDuration: 400,
            fit: true,
            padding: 80,
        } as any).run();
    }

    /** Navigate back to a specific level in the drill stack */
    private drillTo(level: number): void {
        if (!this.cy) return;

        if (level < 0) {
            // Back to root
            this.resetView();
            return;
        }

        // Truncate stack to this level
        this.drillStack = this.drillStack.slice(0, level + 1);
        const entry = this.drillStack[level];
        const node = this.cy.getElementById(entry.nodeId);
        if (node && node.length > 0) {
            // First show all, then apply drill view for this level
            this.cy.elements().style('display', 'element');
            this.applyDrillView(node);
            this.isDrilledDown = true;
            this.renderBreadcrumb();
        } else {
            this.resetView();
        }
    }

    private resetView(): void {
        if (!this.cy) return;
        this.cy.elements().style('display', 'element');
        this.applyHiddenTypes();
        this.runConcentricLayout();
        // Sprint 58: maintain governance state on reset
        this.applyGovernanceVisibility();
        this.isDrilledDown = false;
        this.drillStack = [];
        this.removeBreadcrumb();
    }

    // -----------------------------------------------------------------------
    // Detail panel
    // -----------------------------------------------------------------------

    private showDetail(node: NodeSingular): void {
        this.hideDetail();
        const data = node.data();

        const panel = document.createElement('div');
        panel.id = 'fv-detail';
        panel.style.cssText =
            'width:320px;background:#1e293b;' +
            'border:1px solid #334155;border-radius:10px;padding:16px;z-index:10000;' +
            'max-height:70vh;overflow-y:auto;font-family:system-ui,sans-serif;color:#cbd5e1;';
        // Prevent clicks inside panel from propagating to cytoscape
        panel.addEventListener('click', (e) => e.stopPropagation());
        panel.addEventListener('mousedown', (e) => e.stopPropagation());

        const closeBtn = document.createElement('span');
        closeBtn.textContent = 'X';
        closeBtn.style.cssText =
            'position:absolute;top:8px;right:12px;cursor:pointer;color:#64748b;font-size:18px;';
        closeBtn.onclick = () => this.hideDetail();

        const title = document.createElement('h3');
        title.textContent = data.label;
        title.style.cssText = 'font-size:16px;color:#f1f5f9;margin:0 0 4px;';

        const badge = document.createElement('div');
        badge.textContent = data.entityType;
        badge.style.cssText =
            `display:inline-block;padding:2px 8px;border-radius:12px;font-size:11px;` +
            `font-weight:600;margin-bottom:12px;background:${data.color};color:#0f172a;`;

        panel.append(closeBtn, title, badge);

        // Metadata rows
        const skip = new Set(['id', 'label', 'entityType', 'color', 'isGovernance']);
        for (const [k, v] of Object.entries(data)) {
            if (skip.has(k) || !v || v === 'undefined' || v === 'None') continue;
            const row = document.createElement('div');
            row.style.cssText = 'padding:4px 0;border-bottom:1px solid #1e293b;font-size:12px;';
            const keySpan = k.replace(/[_-]/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
            row.innerHTML = `<span style="color:#64748b">${keySpan}:</span> <span style="color:#cbd5e1;margin-left:4px">${v}</span>`;
            panel.appendChild(row);
        }

        // Connected entities
        const connected = node.connectedEdges();
        if (connected.length > 0) {
            const h4 = document.createElement('h4');
            h4.textContent = `Connections (${connected.length})`;
            h4.style.cssText = 'font-size:13px;color:#94a3b8;margin:12px 0 6px;';
            panel.appendChild(h4);

            connected.forEach((edge) => {
                const other = edge.source().id() === node.id() ? edge.target() : edge.source();
                const item = document.createElement('div');
                item.style.cssText = 'font-size:12px;padding:3px 0;color:#cbd5e1;cursor:pointer;';
                item.innerHTML = `<span style="color:${other.data('color')}">*</span> ${other.data('label')} <span style="color:#64748b;font-size:11px">${edge.data('relationshipType')}</span>`;
                item.onclick = () => {
                    this.cy?.animate({ center: { eles: other }, zoom: 2 } as any, { duration: 400 });
                };
                panel.appendChild(item);
            });
        }

        // Drill-down button
        const drillBtn = document.createElement('button');
        drillBtn.textContent = 'Drill Down (2-hop)';
        drillBtn.style.cssText =
            'margin-top:12px;padding:8px 12px;border-radius:8px;border:1px solid #3b82f6;' +
            'background:#1e3a5f;color:#e2e8f0;font-size:12px;cursor:pointer;width:100%;' +
            'transition:background 0.15s;position:relative;z-index:20;';
        drillBtn.onmouseenter = () => { drillBtn.style.background = '#2563eb'; };
        drillBtn.onmouseleave = () => { drillBtn.style.background = '#1e3a5f'; };
        const handleDrill = (e: Event) => {
            console.log('[Constellation] Drill button clicked!', node?.id());
            e.stopPropagation();
            e.preventDefault();
            this.hideDetail();
            this.drillDown(node);
        };
        drillBtn.addEventListener('click', handleDrill);
        drillBtn.addEventListener('pointerup', handleDrill);
        panel.appendChild(drillBtn);

        // Append detail panel to document body (not cytoscape container) to avoid event swallowing
        // Position it relative to the container
        const containerRect = this.container!.getBoundingClientRect();
        panel.style.position = 'fixed';
        panel.style.top = `${containerRect.top + 60}px`;
        panel.style.right = `${window.innerWidth - containerRect.right + 12}px`;
        document.body.appendChild(panel);
    }

    private hideDetail(): void {
        // Panel may be on document.body
        const el = document.getElementById('fv-detail');
        if (el) el.remove();
    }

    // -----------------------------------------------------------------------
    // Breadcrumb
    // -----------------------------------------------------------------------

    private renderBreadcrumb(): void {
        this.removeBreadcrumb();

        // Use the external breadcrumb row if available, otherwise fall back to overlay
        const row = document.getElementById('smap-breadcrumb-row');
        const useExternal = !!row;

        const bar = document.createElement('div');
        bar.id = 'fv-breadcrumb';

        if (useExternal) {
            bar.style.cssText =
                'display:flex;align-items:center;gap:6px;flex-wrap:wrap;color:#e2e8f0;font-size:13px;';
        } else {
            bar.style.cssText =
                'position:absolute;top:8px;left:8px;background:#1e293b;border:1px solid #334155;' +
                'border-radius:8px;padding:8px 14px;color:#e2e8f0;font-family:system-ui,sans-serif;' +
                'font-size:13px;z-index:10;display:flex;align-items:center;gap:6px;flex-wrap:wrap;max-width:80%;';
        }

        for (const evt of ['mousedown', 'pointerdown', 'touchstart', 'click'] as const) {
            bar.addEventListener(evt, (e) => e.stopPropagation());
        }

        // "All Entities" root link
        const allBtn = document.createElement('span');
        allBtn.textContent = 'All Entities';
        allBtn.style.cssText = 'cursor:pointer;color:#60a5fa;';
        allBtn.onmouseenter = () => { allBtn.style.textDecoration = 'underline'; };
        allBtn.onmouseleave = () => { allBtn.style.textDecoration = 'none'; };
        allBtn.onclick = () => this.resetView();
        bar.appendChild(allBtn);

        // Each level in the stack
        this.drillStack.forEach((entry, i) => {
            const sep = document.createElement('span');
            sep.textContent = ' > ';
            sep.style.color = '#64748b';
            bar.appendChild(sep);

            const isLast = i === this.drillStack.length - 1;
            const crumb = document.createElement('span');
            crumb.textContent = entry.label;

            if (isLast) {
                crumb.style.cssText = 'color:#facc15;font-weight:600;';
            } else {
                crumb.style.cssText = 'cursor:pointer;color:#60a5fa;';
                crumb.onmouseenter = () => { crumb.style.textDecoration = 'underline'; };
                crumb.onmouseleave = () => { crumb.style.textDecoration = 'none'; };
                const level = i;
                crumb.onclick = () => this.drillTo(level);
            }
            bar.appendChild(crumb);
        });

        if (useExternal) {
            row!.innerHTML = '';
            row!.appendChild(bar);
            row!.classList.remove('hidden');
        } else {
            this.container!.appendChild(bar);
        }
    }

    private removeBreadcrumb(): void {
        document.getElementById('fv-breadcrumb')?.remove();
        const row = document.getElementById('smap-breadcrumb-row');
        if (row) {
            row.innerHTML = '';
            row.classList.add('hidden');
        }
    }

    // -----------------------------------------------------------------------
    // Overlays: Legend, Search, Toolbar, Stats
    // -----------------------------------------------------------------------

    private buildOverlays(data: ConstellationData): void {
        this.buildLegend(data);
        this.buildToolbar();
        this.buildStats(data);
    }

    private buildLegend(data: ConstellationData): void {
        const typeCounts = new Map<string, number>();
        for (const n of data.nodes) {
            typeCounts.set(n.entityType, (typeCounts.get(n.entityType) || 0) + 1);
        }

        const legend = document.createElement('div');
        legend.id = 'fv-legend';
        legend.style.cssText =
            'position:absolute;bottom:12px;left:12px;background:rgba(30,41,59,0.92);' +
            'border:1px solid #334155;border-radius:10px;padding:10px 14px;z-index:10;' +
            'font-family:system-ui,sans-serif;font-size:12px;color:#cbd5e1;' +
            'max-height:60vh;overflow-y:auto;max-width:220px;';
        for (const evt of ['mousedown', 'pointerdown', 'touchstart', 'click'] as const) {
            legend.addEventListener(evt, (e) => e.stopPropagation());
        }

        const title = document.createElement('div');
        title.textContent = 'Entity Types';
        title.style.cssText = 'font-weight:600;margin-bottom:6px;color:#f1f5f9;font-size:13px;';
        legend.appendChild(title);

        // Sort by count descending, skip governance types when overlay hidden
        const sorted = Array.from(typeCounts.entries())
            .filter(([type]) => {
                if (!this.governanceVisible && classifyEntityType(type) === 'governance') {
                    return false;
                }
                return true;
            })
            .sort((a, b) => b[1] - a[1]);

        for (const [type, count] of sorted) {
            const color = getEntityColor(type);
            const row = document.createElement('div');
            row.style.cssText =
                'display:flex;align-items:center;gap:8px;padding:3px 0;cursor:pointer;transition:opacity 0.2s;';

            const dot = document.createElement('span');
            dot.style.cssText =
                `display:inline-block;width:12px;height:12px;border-radius:50%;background:${color};flex-shrink:0;`;

            const label = document.createElement('span');
            label.textContent = `${type} (${count})`;

            row.append(dot, label);
            row.onclick = () => this.toggleEntityType(type, row);
            legend.appendChild(row);
        }

        this.container!.appendChild(legend);
    }

    private toggleEntityType(type: string, row: HTMLElement): void {
        if (!this.cy) return;
        if (this.hiddenTypes.has(type)) {
            this.hiddenTypes.delete(type);
            row.style.opacity = '1';
        } else {
            this.hiddenTypes.add(type);
            row.style.opacity = '0.3';
        }
        this.applyHiddenTypes();
    }

    private applyHiddenTypes(): void {
        if (!this.cy) return;
        this.cy.nodes().forEach((n) => {
            const hidden = this.hiddenTypes.has(n.data('entityType'));
            n.style('display', hidden ? 'none' : 'element');
        });
    }

    private buildToolbar(): void {
        const toolbar = document.createElement('div');
        toolbar.id = 'fv-toolbar';
        toolbar.style.cssText =
            'position:absolute;top:12px;right:12px;display:flex;gap:8px;z-index:10;';

        // Prevent Cytoscape from swallowing pointer events on toolbar elements
        for (const evt of ['mousedown', 'pointerdown', 'touchstart', 'click'] as const) {
            toolbar.addEventListener(evt, (e) => e.stopPropagation());
        }

        const btnStyle =
            'padding:6px 12px;border-radius:8px;border:1px solid #334155;background:#1e293b;' +
            'color:#e2e8f0;font-size:13px;cursor:pointer;font-family:system-ui,sans-serif;';

        // Search
        const search = document.createElement('input');
        search.type = 'text';
        search.placeholder = 'Search entities...';
        search.style.cssText = btnStyle + 'width:220px;outline:none;';
        search.onfocus = () => { search.style.borderColor = '#3b82f6'; };
        search.onblur = () => { search.style.borderColor = '#334155'; };
        search.oninput = () => {
            const q = search.value.trim().toLowerCase();
            if (!this.cy) return;
            this.cy.nodes().removeClass('search-match dimmed');
            this.cy.edges().removeClass('dimmed');
            if (!q) return;
            const matches = this.cy.nodes().filter((n) =>
                (n.data('label') as string).toLowerCase().includes(q),
            );
            if (matches.length > 0) {
                this.cy.elements().addClass('dimmed');
                matches.removeClass('dimmed').addClass('search-match');
                matches.connectedEdges().removeClass('dimmed');
                if (matches.length <= 5) {
                    this.cy.animate({ fit: { eles: matches, padding: 100 } } as any, { duration: 400 });
                }
            }
        };

        // Refine button
        const refineBtn = document.createElement('button');
        refineBtn.textContent = 'Refine';
        refineBtn.title = 'Run force-directed refinement';
        refineBtn.style.cssText = btnStyle;
        refineBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.runForceRefinement(refineBtn as HTMLButtonElement);
        });

        // Export PNG
        const exportBtn = document.createElement('button');
        exportBtn.textContent = 'PNG';
        exportBtn.title = 'Export as PNG';
        exportBtn.style.cssText = btnStyle;
        exportBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.exportPNG();
        });

        // Reset
        const resetBtn = document.createElement('button');
        resetBtn.textContent = 'Reset';
        resetBtn.title = 'Reset view';
        resetBtn.style.cssText = btnStyle;
        resetBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            console.log('[Constellation] Reset clicked, isDrilledDown:', this.isDrilledDown);
            if (this.isDrilledDown) {
                this.resetView();
            } else {
                this.hiddenTypes.clear();
                const rows = document.querySelectorAll('#fv-legend div[style*="cursor"]');
                rows.forEach((r) => (r as HTMLElement).style.opacity = '1');
                this.cy?.elements().style('display', 'element');
                this.cy?.nodes().removeClass('search-match dimmed');
                this.cy?.edges().removeClass('dimmed');
                // Sprint 57: clear group filter classes
                this.cy?.elements().removeClass('group-dimmed group-highlighted');
                search.value = '';
                // Sprint 58: re-apply governance visibility after full reset
                this.applyGovernanceVisibility();
                this.cy?.animate({ fit: { eles: this.cy!.elements(), padding: 50 } } as any, { duration: 500 });
            }
            // Notify external listeners (e.g. controller) that reset occurred
            this.container?.dispatchEvent(new CustomEvent('visualizer-reset'));
        });

        toolbar.append(search, refineBtn, exportBtn, resetBtn);
        this.container!.appendChild(toolbar);
    }

    private buildStats(data: ConstellationData): void {
        const types = new Set(data.nodes.map((n) => n.entityType));
        const stats = document.createElement('div');
        stats.id = 'fv-stats';
        stats.style.cssText =
            'position:absolute;bottom:12px;right:12px;background:rgba(30,41,59,0.92);' +
            'border:1px solid #334155;border-radius:10px;padding:10px 14px;z-index:10;' +
            'font-size:12px;color:#94a3b8;font-family:system-ui,sans-serif;';
        stats.innerHTML =
            `<b style="color:#f1f5f9">${data.nodes.length}</b> entities &middot; ` +
            `<b style="color:#f1f5f9">${data.edges.length}</b> relationships &middot; ` +
            `<b style="color:#f1f5f9">${types.size}</b> types`;
        this.container!.appendChild(stats);
    }

    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------

    private escapeHtml(s: string): string {
        return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
}
